import React, { useEffect } from 'react';
import { Button } from './ui/button';
import { X, Lock, Star } from 'lucide-react';

/**
 * PointsBadge — small inline badge for Ihsan Points.
 */
export const PointsBadge = ({ points = 0, className = '' }) => (
  <span
    data-testid="points-badge"
    className={`inline-flex items-center gap-1 rounded-full bg-white border-2 border-[#D4A843] text-[13px] font-semibold text-foreground px-2.5 py-[3px] ${className}`}
  >
    <Star className="h-3.5 w-3.5 text-[#D4A843] fill-[#D4A843]" />
    {points} pts
  </span>
);

// Shared base badges — every persona sees these
const BASE_EARNED = [
  { emoji: '🌱', title: 'First Step',       note: 'Joined the community' },
  { emoji: '📋', title: 'Profile Complete', note: 'Filled in full profile' },
  { emoji: '🤝', title: 'First Referral',   note: 'Referred first person' }
];

const EARNED_BY_PERSONA = {
  beneficiary: [
    { emoji: '📚', title: 'Eager Learner', note: 'Completed first course' },
    { emoji: '✅', title: 'Timely',        note: 'Submitted all forms on time' },
    { emoji: '🕌', title: 'Faith Journey', note: '30 days Quran log' }
  ],
  volunteer: [
    { emoji: '💪', title: 'Community Helper', note: '10 hrs served' },
    { emoji: '🔥', title: 'Streak Builder',   note: '3 shifts in a row' },
    { emoji: '🌟', title: 'Senior Volunteer', note: '30 hrs served' }
  ],
  donor: [
    { emoji: '💛', title: 'First Pledge',          note: 'First sponsorship' },
    { emoji: '🏆', title: 'CSR Champion',          note: '10+ beneficiaries impacted' },
    { emoji: '🌍', title: 'Community Multiplier',  note: '5+ referrals' }
  ]
};
EARNED_BY_PERSONA.all = [...EARNED_BY_PERSONA.beneficiary, ...EARNED_BY_PERSONA.volunteer, ...EARNED_BY_PERSONA.donor];

const LOCKED_BY_PERSONA = {
  beneficiary: [
    { emoji: '🎓', title: 'Course Streak',   unlock: 'Complete 3 courses' },
    { emoji: '🏅', title: 'Community Award', unlock: 'Reach 500 Ihsan Points' }
  ],
  volunteer: [
    { emoji: '👑', title: 'Lead Volunteer',  unlock: '50 hrs served' },
    { emoji: '📣', title: 'Voice of Ihsan',  unlock: 'Speak at 1 roadshow' },
    { emoji: '🎗️', title: 'Mentor',           unlock: 'Onboard 5 new volunteers' }
  ],
  donor: [
    { emoji: '🌟', title: 'Platinum Partner', unlock: '$10,000 sponsored' },
    { emoji: '🤲', title: 'Legacy Giver',     unlock: '3-year partnership' }
  ]
};
LOCKED_BY_PERSONA.all = [...LOCKED_BY_PERSONA.beneficiary, ...LOCKED_BY_PERSONA.volunteer, ...LOCKED_BY_PERSONA.donor];

const PERSONA_SUBTITLE = {
  beneficiary: 'Earn badges by engaging with services, learning, and community.',
  volunteer:   'Earn badges through service hours, training, and referrals.',
  donor:       'Earn badges through sponsorships, CSR hours, and impact milestones.',
  all:         'Earn badges through learning, volunteering, and giving.'
};

/**
 * BadgeDrawer — full-screen slide-up drawer showing earned + locked badges.
 */
export const BadgeDrawer = ({ isOpen, onClose, persona = 'all' }) => {
  // Prevent body scroll while open
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const earned = [...BASE_EARNED, ...(EARNED_BY_PERSONA[persona] || [])];
  const locked = LOCKED_BY_PERSONA[persona] || [];
  const subtitle = PERSONA_SUBTITLE[persona] || PERSONA_SUBTITLE.beneficiary;

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Slide-up panel */}
      <div
        data-testid="badge-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Your Ihsan Badges"
        className={`fixed inset-x-0 bottom-0 mx-auto max-w-[480px] z-50 bg-background rounded-t-3xl shadow-2xl max-h-[90vh] flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {/* Grabber handle */}
        <div className="pt-3 pb-1 flex justify-center flex-shrink-0">
          <span className="w-10 h-1.5 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Header */}
        <div className="px-5 pb-4 flex items-start justify-between gap-3 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold leading-tight">Your Ihsan Badges</h2>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 w-9 h-9 rounded-full bg-muted hover:bg-muted/70 flex items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-5">
          {/* Earned */}
          <section>
            <p className="text-[12px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">
              Earned · {earned.length}
            </p>
            <div className="grid grid-cols-3 gap-3">
              {earned.map((b, i) => (
                <div
                  key={`e-${i}`}
                  data-testid={`badge-earned-${i}`}
                  className="rounded-xl border-2 border-[#D4A843]/40 bg-amber-50 p-3 text-center flex flex-col items-center gap-1"
                >
                  <span className="text-3xl leading-none" aria-hidden="true">{b.emoji}</span>
                  <p className="text-[12px] font-bold leading-tight mt-1">{b.title}</p>
                  <p className="text-[10px] text-muted-foreground leading-tight">{b.note}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Locked */}
          <section>
            <p className="text-[12px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">
              Locked · {locked.length}
            </p>
            <div className="grid grid-cols-3 gap-3">
              {locked.map((b, i) => (
                <div
                  key={`l-${i}`}
                  data-testid={`badge-locked-${i}`}
                  className="relative rounded-xl border-2 border-dashed border-border bg-muted/30 p-3 text-center flex flex-col items-center gap-1"
                >
                  <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-background border flex items-center justify-center">
                    <Lock className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <span className="text-3xl leading-none grayscale opacity-50" aria-hidden="true">{b.emoji}</span>
                  <p className="text-[12px] font-bold leading-tight text-muted-foreground mt-1">{b.title}</p>
                  <p className="text-[10px] text-muted-foreground leading-tight italic">{b.unlock}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Close button */}
        <div className="px-5 py-4 border-t bg-background flex-shrink-0">
          <Button
            data-testid="badge-drawer-close"
            onClick={onClose}
            size="lg"
            className="w-full h-11"
          >
            Close
          </Button>
        </div>
      </div>
    </>
  );
};
